import { AdfInvalidNumberError, AdfUnexpectedCharacterError } from './errors';
import type { Repair, Token, TokenType } from './types';

export class AdfTokenizer {
	private position: number = 0;
	private lastTokenType: TokenType | null = null;

	// Characters that indicate a quote should close the string
	// Includes standard delimiters and structural chars (for malformed JSON with missing braces)
	private static readonly CLOSING_DELIMITERS = new Set([',', '}', ']', ':', '{', '[']);

	// eslint-disable-next-line no-useless-constructor
	constructor(
		private input: string,
		private onRepair?: (repair: Repair) => void,
		private ignoreUnknownCharacters: boolean = false,
	) {}

	getPosition(): number {
		return this.position;
	}

	reset(input: string, position: number = 0): void {
		this.input = input;
		this.position = position;
		this.lastTokenType = null;
	}

	private isWhitespace(char: string): boolean {
		return [' ', '\n', '\t', '\r'].includes(char);
	}

	private isExpectingKey(): boolean {
		return this.lastTokenType === 'LEFT_BRACE' || this.lastTokenType === 'COMMA';
	}

	private isDigit(char: string): boolean {
		return char >= '0' && char <= '9';
	}

	private isAlpha(char: string): boolean {
		return (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z');
	}

	private isHexDigit(char: string): boolean {
		return (
			(char >= '0' && char <= '9') || (char >= 'a' && char <= 'f') || (char >= 'A' && char <= 'F')
		);
	}

	private peek(): string {
		return this.position < this.input.length ? this.input[this.position] || '\0' : '\0';
	}

	private advance(): string {
		if (this.position < this.input.length) {
			return this.input[this.position++] || '\0';
		}
		return '\0';
	}

	private skipWhitespace(): void {
		while (this.position < this.input.length && this.isWhitespace(this.peek())) {
			this.advance();
		}
	}

	/**
	 * Check if a quote is actually closing the string by looking at what follows
	 * A closing quote should be followed by: comma, right brace, right bracket, or colon
	 * (with optional whitespace in between)
	 */
	private isClosingQuote(position: number): boolean {
		let i = position + 1; // Skip the quote itself
		// Skip whitespace
		while (i < this.input.length && this.isWhitespace(this.input[i] || '')) {
			i++;
		}
		// Check if followed by valid JSON delimiters or structural characters
		if (i < this.input.length) {
			const nextChar = this.input[i];
			return AdfTokenizer.CLOSING_DELIMITERS.has(nextChar || '');
		}
		// At EOF, consider it closing
		return true;
	}

	private readString(): Token {
		const start = this.position;
		this.advance(); // consume opening quote

		let value = '';
		let hasUnescapedQuotes = false;

		while (this.position < this.input.length) {
			if (this.peek() === '\\') {
				this.advance(); // consume backslash
				const escaped = this.advance();
				switch (escaped) {
					case '"':
						value += '"';
						break;
					case '\\':
						value += '\\';
						break;
					case '/':
						value += '/';
						break;
					case 'b':
						value += '\b';
						break;
					case 'f':
						value += '\f';
						break;
					case 'n':
						value += '\n';
						break;
					case 'r':
						value += '\r';
						break;
					case 't':
						value += '\t';
						break;
					case 'u': {
						// Read hex digits (keep raw if incomplete for streaming)
						let hex = '';
						for (let i = 0; i < 4; i++) {
							const ch = this.peek();
							if (this.isHexDigit(ch)) {
								hex += this.advance();
							} else {
								break;
							}
						}
						if (hex.length < 4) {
							value += '\\u' + hex;
							break;
						}

						const codeUnit = parseInt(hex, 16);
						// Handle surrogate pairs: \uD8xx\uDCxx
						if (codeUnit >= 0xd800 && codeUnit <= 0xdbff) {
							// Lookahead for low-surrogate
							if (
								this.position + 6 <= this.input.length &&
								this.input[this.position] === '\\' &&
								this.input[this.position + 1] === 'u'
							) {
								const lowHex = this.input.substring(this.position + 2, this.position + 6);
								// eslint-disable-next-line require-unicode-regexp
								if (/^[0-9a-fA-F]{4}$/.test(lowHex)) {
									const low = parseInt(lowHex, 16);
									if (low >= 0xdc00 && low <= 0xdfff) {
										// Valid surrogate pair, consume and combine
										this.advance(); // backslash
										this.advance(); // 'u'
										for (let i = 0; i < 4; i++) {
											this.advance();
										}
										// eslint-disable-next-line no-bitwise
										const codePoint = ((codeUnit - 0xd800) << 10) + (low - 0xdc00) + 0x10000;
										value += String.fromCodePoint(codePoint);
										break;
									}
								}
							}
							// No valid low surrogate; emit high surrogate code unit as-is
							value += String.fromCharCode(codeUnit);
						} else if (codeUnit >= 0xdc00 && codeUnit <= 0xdfff) {
							// Lone low surrogate; emit as-is
							value += String.fromCharCode(codeUnit);
						} else {
							value += String.fromCharCode(codeUnit);
						}
						break;
					}
					default:
						value += escaped;
						break;
				}
			} else if (this.peek() === '"') {
				// For object keys, always treat quote as closing (strict parsing for missing colon detection)
				// For values, check if it's a real closing quote or unescaped quote inside
				if (this.isExpectingKey() || this.isClosingQuote(this.position)) {
					break;
				} else {
					// Unescaped quote inside a value string - treat as literal character
					value += this.advance();
					hasUnescapedQuotes = true;
				}
			} else {
				value += this.advance();
			}
		}

		// Handle EOF mid-string for streaming (return partial, more data may come)
		if (this.peek() === '"') {
			this.advance(); // consume closing quote
		}

		// Report repair if we found unescaped quotes
		if (hasUnescapedQuotes) {
			this.onRepair?.({
				type: 'infer',
				from: 'unescaped_quote',
				to: 'escaped_quote',
				context: 'tokenizer_string',
			});
		}

		return {
			type: 'STRING',
			value,
			position: start,
		};
	}

	private readNumber(): Token {
		const start = this.position;
		let value = '';

		// Handle negative sign
		if (this.peek() === '-') {
			value += this.advance();
		}

		// Handle integer part
		if (this.peek() === '0') {
			value += this.advance();
		} else if (this.isDigit(this.peek())) {
			while (this.isDigit(this.peek())) {
				value += this.advance();
			}
		} else {
			throw new AdfInvalidNumberError(this.position);
		}

		// Handle decimal part
		if (this.peek() === '.') {
			value += this.advance();
			if (!this.isDigit(this.peek())) {
				throw new AdfInvalidNumberError(this.position);
			}
			while (this.isDigit(this.peek())) {
				value += this.advance();
			}
		}

		// Handle exponent
		if (this.peek() === 'e' || this.peek() === 'E') {
			value += this.advance();
			if (this.peek() === '+' || this.peek() === '-') {
				value += this.advance();
			}
			if (!this.isDigit(this.peek())) {
				throw new AdfInvalidNumberError(this.position);
			}
			while (this.isDigit(this.peek())) {
				value += this.advance();
			}
		}

		return {
			type: 'NUMBER',
			value,
			position: start,
		};
	}

	private readLiteral(): Token {
		const start = this.position;
		let value = '';

		while (this.position < this.input.length && this.isAlpha(this.peek())) {
			value += this.advance();
		}

		// Complete partial literals at EOF for streaming (t→true, f→false, n→null)
		const atEOF = this.position >= this.input.length;
		if (atEOF && value.length > 0 && value.length < 5) {
			const originalValue = value;
			if ('true'.startsWith(value)) {
				value = 'true';
			} else if ('false'.startsWith(value)) {
				value = 'false';
			} else if ('null'.startsWith(value)) {
				value = 'null';
			}

			if (originalValue !== value) {
				this.onRepair?.({
					type: 'infer',
					from: originalValue,
					to: value,
					context: 'tokenizer_partial_literal',
				});
			}
		}

		// Handle JSON literals
		if (value === 'true' || value === 'false') {
			return { type: 'BOOLEAN', value: value === 'true' ? 'true' : 'false', position: start };
		}
		if (value === 'null') {
			return { type: 'NULL', value: 'null', position: start };
		}

		// Check for misplaced quote after identifier: content"  :
		// Skip the quote if it's followed by whitespace and a colon
		if (this.peek() === '"') {
			const quotePos = this.position;
			this.advance(); // Skip the quote
			this.skipWhitespace();
			if (this.peek() === ':') {
				// This was a misplaced quote, we've successfully skipped it
				this.onRepair?.({
					type: 'skip',
					from: 'misplaced_quote',
					to: '-',
					context: 'tokenizer_key',
				});
				// Continue and return the identifier as a STRING token
			} else {
				// Not a misplaced quote pattern, restore position
				this.position = quotePos;
			}
		}

		// Treat any other unquoted identifier as a string value (lenient parsing)
		// This handles cases like: {"key": world} → {"key": "world"}
		this.onRepair?.({
			type: 'infer',
			from: value,
			to: `"${value}"`,
			context: 'tokenizer_unquoted_string',
		});
		return { type: 'STRING', value, position: start };
	}

	private createSingleCharToken<T extends TokenType>(type: T, value: string): Token {
		const position = this.position;
		this.advance();
		return { type, value, position } as Token;
	}

	nextToken(): Token {
		this.skipWhitespace();

		if (this.position >= this.input.length) {
			const token = { type: 'EOF' as const, value: '' as const, position: this.position };
			this.lastTokenType = token.type;
			return token;
		}

		const char = this.peek();

		let token: Token;
		switch (char) {
			case '{':
				token = this.createSingleCharToken('LEFT_BRACE', '{');
				break;
			case '}':
				token = this.createSingleCharToken('RIGHT_BRACE', '}');
				break;
			case '[':
				token = this.createSingleCharToken('LEFT_BRACKET', '[');
				break;
			case ']':
				token = this.createSingleCharToken('RIGHT_BRACKET', ']');
				break;
			case ':':
				token = this.createSingleCharToken('COLON', ':');
				break;
			case ',':
				token = this.createSingleCharToken('COMMA', ',');
				break;
			case '"':
				token = this.readString();
				break;
			case '-':
			case '0':
			case '1':
			case '2':
			case '3':
			case '4':
			case '5':
			case '6':
			case '7':
			case '8':
			case '9':
				token = this.readNumber();
				break;
			default:
				if (this.isAlpha(char)) {
					token = this.readLiteral();
				} else if (this.ignoreUnknownCharacters) {
					// Skip unknown characters and emit repair event (lenient parsing)
					this.onRepair?.({
						type: 'skip',
						from: char,
						to: '-',
						context: 'tokenizer_unknown_char',
					});
					this.advance();
					// Recursively get the next valid token
					return this.nextToken();
				} else {
					throw new AdfUnexpectedCharacterError(char, this.position);
				}
		}

		this.lastTokenType = token.type;
		return token;
	}
}
